/*
  PRODUCT IMAGE VIEWER

  Tapping a product photograph opens it over the page. What the viewer does
  is set by the reference shop: a counter on the left, a dark toolbar on the
  right holding zoom, slideshow, full screen, thumbnails and close, a round
  arrow on either side, and a strip of thumbnails along the bottom. A swipe
  moves between photographs, a pinch or a double tap zooms into one, and
  dragging moves a zoomed photograph around.

  It reads the gallery itself: every [data-zoom-full] inside the product
  gallery is one photograph, in the order they are already shown. Videos
  carry no such attribute, so they are passed over rather than opened.
*/
(function () {
  'use strict';
  if (window.__refLightbox) return;
  window.__refLightbox = true;

  // Both measured off the reference recording, frame by frame: it rests on a
  // photograph for about two and a half seconds, and the change from one to
  // the next is a cross-fade lasting about an eighth of a second.
  var SLIDE_MS = 2400;
  var FADE_MS = 130;
  var ZOOM = 2.6;               // how far the zoom button goes
  var TAP_SLOP = 10;            // a tap that moves further than this is a swipe
  var SWIPE_MIN = 45;

  var ICONS = {
    zoomIn: '<circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.4 15.4 21 21M10.5 7.8v5.4M7.8 10.5h5.4"/>',
    zoomOut: '<circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.4 15.4 21 21M7.8 10.5h5.4"/>',
    play: '<path d="M7 4.5 19 12 7 19.5Z"/>',
    pause: '<path d="M8.5 4.5v15M15.5 4.5v15"/>',
    full: '<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/>',
    thumbs: '<rect x="3.5" y="4" width="17" height="11.5" rx="1.5"/><path d="M6 19h.01M10 19h.01M14 19h.01M18 19h.01"/>',
    close: '<path d="M5 5l14 14M19 5 5 19"/>',
    prev: '<path d="M14.5 5 8 12l6.5 7"/>',
    next: '<path d="M9.5 5 16 12l-6.5 7"/>'
  };

  function svg(paths) {
    return '<svg viewBox="0 0 24 24" aria-hidden="true">' + paths + '</svg>';
  }

  function build() {
    var el = document.createElement('div');
    el.className = 'rlb is-thumbs-on';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Product photographs');
    el.tabIndex = -1;          /* holds the keyboard without ringing a button */
    el.innerHTML =
      '<div class="rlb__stage" data-rlb-stage></div>' +
      '<p class="rlb__count rlb__chrome" data-rlb-count></p>' +
      '<div class="rlb__bar rlb__chrome">' +
        '<button class="rlb__btn" type="button" data-rlb-zoom aria-label="Zoom">' +
          '<span data-when="out" class="is-on">' + svg(ICONS.zoomIn) + '</span>' +
          '<span data-when="in">' + svg(ICONS.zoomOut) + '</span>' +
        '</button>' +
        '<button class="rlb__btn" type="button" data-rlb-play aria-label="Slideshow">' +
          '<span data-when="stopped" class="is-on">' + svg(ICONS.play) + '</span>' +
          '<span data-when="playing">' + svg(ICONS.pause) + '</span>' +
        '</button>' +
        '<button class="rlb__btn" type="button" data-rlb-full aria-label="Full screen">' + svg(ICONS.full) + '</button>' +
        '<button class="rlb__btn" type="button" data-rlb-thumbs-toggle aria-label="Thumbnails">' + svg(ICONS.thumbs) + '</button>' +
        '<button class="rlb__btn" type="button" data-rlb-close aria-label="Close">' + svg(ICONS.close) + '</button>' +
      '</div>' +
      '<div class="rlb__nav-layer rlb__chrome">' +
        '<button class="rlb__nav rlb__nav--prev" type="button" data-rlb-prev aria-label="Previous">' + svg(ICONS.prev) + '</button>' +
        '<button class="rlb__nav rlb__nav--next" type="button" data-rlb-next aria-label="Next">' + svg(ICONS.next) + '</button>' +
      '</div>' +
      '<div class="rlb__thumbs rlb__chrome" data-rlb-thumbs></div>';
    document.body.appendChild(el);
    return el;
  }

  var box = null, stage, countEl, thumbsEl, prevBtn, nextBtn, zoomBtn, playBtn;
  var shots = [], at = 0, scale = 1, panX = 0, panY = 0, timer = null, opener = null;
  var sources = [], shutting = false, session = 0;

  function ensure() {
    if (box) return;
    box = build();
    stage = box.querySelector('[data-rlb-stage]');
    countEl = box.querySelector('[data-rlb-count]');
    thumbsEl = box.querySelector('[data-rlb-thumbs]');
    prevBtn = box.querySelector('[data-rlb-prev]');
    nextBtn = box.querySelector('[data-rlb-next]');
    zoomBtn = box.querySelector('[data-rlb-zoom]');
    playBtn = box.querySelector('[data-rlb-play]');
    wire();
  }

  /* A viewer nailed to the screen, whatever the page does.

     `position:fixed` stops meaning "against the screen" as soon as any
     ancestor carries a transform, a filter or containment — and large themes
     do that all the time, for a hover effect or a scroll trick. The viewer
     would then be measured against the page instead, and on a page scrolled
     halfway down it would open somewhere off the screen entirely. So the
     viewer measures itself once it is open and, if it did not land on the
     screen's own corner, shifts itself there. */
  function anchor() {
    if (!box) return;
    box.style.removeProperty('transform');
    box.style.removeProperty('width');
    box.style.removeProperty('height');
    var r = box.getBoundingClientRect();
    var w = window.innerWidth, h = window.innerHeight;
    if (Math.abs(r.left) < 2 && Math.abs(r.top) < 2 &&
        Math.abs(r.width - w) < 2 && Math.abs(r.height - h) < 2) return;
    box.style.setProperty('width', w + 'px', 'important');
    box.style.setProperty('height', h + 'px', 'important');
    box.style.setProperty('transform',
      'translate(' + (-r.left) + 'px,' + (-r.top) + 'px)', 'important');
  }

  /* ONE piece of machinery for every bit of movement in the viewer.

     None of it is left to CSS. A theme of any size is liable to carry a
     blanket `transition:none`, and a computer set to show fewer animations
     switches off smooth scrolling as well — on the shop that turned every
     change into a bare jump with nothing to see. What follows cannot be
     switched off that way: the browser's own animation engine is used when
     it works, and when it does not, the movement is drawn by hand, frame by
     frame.                                                                  */

  function clock() {
    return (window.performance && performance.now) ? performance.now() : Date.now();
  }

  /* One number eased to another. `put` receives the value and whether this is
     the last frame, and may return false to abandon a movement that a newer
     one has overtaken. */
  function tween(from, to, ms, put) {
    var t0 = clock();
    var frame = function () {
      var k = Math.min(1, (clock() - t0) / ms);
      var e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      if (put(from + (to - from) * e, k >= 1) === false) return;
      if (k < 1) requestAnimationFrame(frame);
      else put(to, true);
    };
    requestAnimationFrame(frame);
  }

  /* An element faded from one opacity to another. */
  function fadeTo(el, from, to, ms) {
    if (!el) return;
    el.style.setProperty('opacity', to);
    if (from === to) return;
    var byHand = function () {
      tween(from, to, ms, function (v, done) {
        el.style.setProperty('opacity', done ? to : v);
      });
    };
    var running = null;
    try {
      if (el.animate) {
        running = el.animate([{ opacity: from }, { opacity: to }],
                             { duration: ms, easing: 'ease' });
      }
    } catch (e) { running = null; }
    if (!running) { byHand(); return; }
    // Trust nothing: on the very next frame the opacity must be somewhere
    // between the two. If it is already at the far end, the animation never
    // took hold, and the fade is drawn by hand instead.
    requestAnimationFrame(function () {
      if (Math.abs(parseFloat(getComputedStyle(el).opacity) - to) < 0.01) byHand();
    });
  }

  /* The counter, the toolbar, the arrows and the thumbnails, which come and go
     with the viewer itself. */
  function fade(el, from, to, ms) {
    if (!el) return;
    el.style.removeProperty('opacity');
    fadeTo(el, from, to, ms);
    setTimeout(function () {
      if (to > 0.5 && !(parseFloat(getComputedStyle(el).opacity) > 0.9)) {
        el.style.setProperty('opacity', '1', 'important');
      }
    }, ms + 120);
  }

  /* The dark behind the photograph.

     It is painted on the viewer itself rather than on a child of it. A child
     can fail in ways a whole page's worth of theme CSS makes easy — it can be
     given an opacity, be emptied of its background, be left out of the paint
     order — and on the live shop exactly that happened: the viewer opened
     over a bright page with white icons on white. The colour is now set
     inline and !important, which no stylesheet can undo, and it is faded by
     animating the colour rather than an opacity, so the photograph and the
     buttons sitting on top of it are untouched by the fade.                  */
  var DARK = 'rgba(0,0,0,.86)';

  function dark(on, ms) {
    if (!box) return;
    // Plain at first, so the fade can run: an !important declaration outranks
    // an animation and would snap the dark on in one frame.
    box.style.setProperty('background-color', on ? DARK : 'transparent');
    try {
      if (box.animate) {
        box.animate([{ backgroundColor: on ? 'rgba(0,0,0,0)' : DARK },
                     { backgroundColor: on ? DARK : 'rgba(0,0,0,0)' }],
                    { duration: ms, easing: 'ease' });
      }
    } catch (e) { /* the colour is already set; only the fade is lost */ }
    if (!on) return;
    // Once the fade is over the dark is nailed down, so that a stylesheet
    // which took it away cannot leave the viewer sitting on a bright page.
    setTimeout(function () {
      if (!box.classList.contains('is-open')) return;
      var now = getComputedStyle(box).backgroundColor;
      var a = now.indexOf('rgba') === 0 ? parseFloat(now.split(',')[3]) : 1;
      if (!(a > 0.7)) box.style.setProperty('background-color', DARK, 'important');
    }, ms + 120);
  }

  function chrome() {
    return box ? [].slice.call(box.querySelectorAll('.rlb__chrome')) : [];
  }

  function face(btn, which) {
    btn.querySelectorAll('[data-when]').forEach(function (s) {
      s.classList.toggle('is-on', s.getAttribute('data-when') === which);
    });
  }

  /* ---------- zoom and pan ---------- */
  function clampPan() {
    var img = stage.querySelector('.rlb__slide.is-active img');
    if (!img) return;
    var r = img.getBoundingClientRect();
    var w = r.width / scale, h = r.height / scale;   // the unzoomed size
    var overX = Math.max(0, (w * scale - stage.clientWidth) / 2);
    var overY = Math.max(0, (h * scale - stage.clientHeight) / 2);
    panX = Math.max(-overX, Math.min(overX, panX));
    panY = Math.max(-overY, Math.min(overY, panY));
  }

  function paint() {
    var img = stage.querySelector('.rlb__slide.is-active img');
    if (!img) return;
    img.style.transformOrigin = 'center center';
    img.style.transform = 'translate(' + panX + 'px,' + panY + 'px) scale(' + scale + ')';
    box.classList.toggle('is-zoomed', scale > 1.01);
    face(zoomBtn, scale > 1.01 ? 'in' : 'out');
  }

  function setZoom(next, originX, originY) {
    var was = scale;
    scale = Math.max(1, Math.min(6, next));
    if (scale <= 1.01) {
      scale = 1;
      panX = panY = 0;
    } else if (originX != null) {
      // keep the point under the finger where it was
      var k = scale / was;
      var cx = stage.clientWidth / 2, cy = stage.clientHeight / 2;
      panX = (panX + cx - originX) * k - cx + originX;
      panY = (panY + cy - originY) * k - cy + originY;
    }
    clampPan();
    paint();
  }

  /* ---------- moving between photographs ---------- */
  function show(i, instant) {
    if (!shots.length) return;
    at = (i + shots.length) % shots.length;
    scale = 1; panX = panY = 0;

    stage.querySelectorAll('.rlb__slide').forEach(function (s, n) {
      var on = n === at;
      s.classList.toggle('is-active', on);
      if (!on) {
        var im = s.querySelector('img');
        if (im) im.style.transform = '';
      }
      crossFade(s, on, instant);
    });
    // a neighbour is fetched early so the next step is instant
    [at - 1, at + 1].forEach(function (n) {
      var s = stage.children[(n + shots.length) % shots.length];
      var im = s && s.querySelector('img');
      if (im && !im.src) im.src = im.dataset.src;
    });

    countEl.textContent = (at + 1) + ' / ' + shots.length;
    thumbsEl.querySelectorAll('.rlb__thumb').forEach(function (t, n) {
      var was = t.getBoundingClientRect().width;
      t.classList.toggle('is-active', n === at);
      widen(t, was, instant);
    });
    var live = thumbsEl.querySelector('.rlb__thumb.is-active');
    if (live) {
      var want = live.offsetLeft - (thumbsEl.clientWidth - live.offsetWidth) / 2;
      var max = thumbsEl.scrollWidth - thumbsEl.clientWidth;
      slideStrip(Math.max(0, Math.min(max, want)), instant);
    }

    var many = shots.length > 1;
    prevBtn.hidden = !many;
    nextBtn.hidden = !many;
    paint();
  }

  /* The thumbnail in use grows, and the strip slides along so that it comes to
     the middle. Together these are the movement a shopper actually sees when
     the slideshow steps, and in the reference both take about as long as the
     photographs' own cross-fade. Both are animated here rather than in CSS:
     a `transition:none` in the theme, or a phone set to show fewer
     animations, would otherwise turn the whole thing into a bare jump —
     which is exactly what the shop was showing. */
  function widen(el, from, instant) {
    var to = el.getBoundingClientRect().width;
    if (instant || Math.abs(to - from) < 1) { el.style.width = ''; return; }
    tween(from, to, FADE_MS, function (v, done) {
      el.style.width = done ? '' : v + 'px';
    });
  }

  function slideStrip(to, instant) {
    var from = thumbsEl.scrollLeft;
    if (instant || Math.abs(to - from) < 1) { thumbsEl.scrollLeft = to; return; }
    thumbsEl._rlbTo = to;
    tween(from, to, FADE_MS, function (v) {
      if (thumbsEl._rlbTo !== to) return false;      // a newer slide took over
      thumbsEl.scrollLeft = v;
    });
  }

  function step(d) { show(at + d); }

  /* One photograph gives way to the next.

     The fade is run by the script rather than by a CSS transition. A theme of
     any size is liable to carry a blanket `transition:none`, and where one
     does the change became a single jump with nothing to see — which is
     exactly what the shop showed. An animation cannot be switched off that
     way, and the opacity is written inline so that it stays put once the
     animation is over. */
  function crossFade(el, on, instant) {
    var to = on ? 1 : 0;
    var from = parseFloat(getComputedStyle(el).opacity);
    if (isNaN(from)) from = to;
    // Plain while the fade runs — an !important declaration outranks an
    // animation and would turn the fade back into a jump.
    el.style.setProperty('opacity', to);
    settle(el, to, instant ? 0 : FADE_MS + 40);
    if (instant || from === to) return;
    fadeTo(el, from, to, FADE_MS);
  }

  /* Once the fade is over the value is nailed down, so that a stylesheet
     cannot leave every photograph on show at once. */
  function settle(el, to, ms) {
    clearTimeout(el._rlbSettle);
    el._rlbSettle = setTimeout(function () {
      el.style.setProperty('opacity', to, 'important');
    }, ms);
  }

  /* ---------- slideshow ---------- */
  function playing() { return timer !== null; }
  function stopPlay() {
    if (timer) clearInterval(timer);
    timer = null;
    face(playBtn, 'stopped');
  }
  function startPlay() {
    if (shots.length < 2) return;
    stopPlay();
    timer = setInterval(function () { step(1); }, SLIDE_MS);
    face(playBtn, 'playing');
  }

  /* ---------- open and close ---------- */
  function open(list, index, from) {
    ensure();
    shutting = false;
    session += 1;                 /* a viewer opened again cancels the last teardown */
    box.classList.remove('is-closing');
    shots = list;
    sources = list.map(function (s) { return s.node || null; });
    opener = from || null;
    stage.innerHTML = '';
    thumbsEl.innerHTML = '';
    shots.forEach(function (s, n) {
      var slide = document.createElement('div');
      slide.className = 'rlb__slide';
      var img = document.createElement('img');
      img.alt = s.alt || '';
      img.dataset.src = s.full;
      img.decoding = 'async';
      if (n === index) {
        // The picture already on the page is in the cache, so it can be shown
        // this instant and the zoom can begin without waiting on the network.
        // The full-size one quietly takes its place as soon as it arrives.
        img.src = s.preview || s.full;
        if (s.preview && s.full !== s.preview) {
          var big = new Image();
          big.onload = function () { img.src = s.full; };
          big.src = s.full;
        }
      }
      slide.appendChild(img);
      stage.appendChild(slide);

      var t = document.createElement('button');
      t.type = 'button';
      t.className = 'rlb__thumb';
      t.setAttribute('aria-label', 'Photograph ' + (n + 1));
      var ti = document.createElement('img');
      ti.src = s.thumb || s.full;
      ti.alt = '';
      ti.loading = 'lazy';
      t.appendChild(ti);
      t.addEventListener('click', function () { stopPlay(); show(n); });
      thumbsEl.appendChild(t);
    });

    box.classList.add('is-open');
    box.classList.remove('is-thumbs-off');
    document.documentElement.classList.add('rlb-lock');
    show(index, true);
    // The viewer must be nailed to the screen BEFORE the zoom is worked out:
    // the zoom is the distance between where the photograph sits on the page
    // and where it will sit in the viewer, and on a page whose body carries a
    // transform the second of those two is wrong until the viewer has been
    // put in its place. Measuring first sent the photograph flying in from
    // somewhere off the screen.
    anchor();
    zoomFrom(sources[index]);

    dark(true, 260);
    chrome().forEach(function (el) {
      el.style.removeProperty('opacity');
      fade(el, 0, 1, 220);
    });
    box.focus({ preventScroll: true });
  }

  /* The opening move: the photograph starts exactly where it sits on the page
     and grows into its place in the viewer, while the page darkens behind it.
     If the picture has no size yet there is nothing to grow from, so the
     viewer simply fades in. */
  /* Moving the photograph from one place to another.

     Like the cross-fade, this is run as an animation rather than as a CSS
     transition. A theme's blanket `transition:none`, or a computer with
     animations turned off in its own settings, switches CSS transitions off
     — and with them went the zoom that carries the photograph out of the page
     and back into it. An animation is not switched off that way, and the end
     state is written inline so it holds once the animation is over. */
  function move(el, from, to, ms, ease) {
    el.style.transform = to;
    try {
      if (el.animate) {
        el.animate([{ transform: from }, { transform: to }],
                   { duration: ms, easing: ease, fill: 'backwards' });
        return;
      }
    } catch (e) { /* the photograph is already in its place */ }
  }

  function zoomFrom(node) {
    var img = stage.querySelector('.rlb__slide.is-active img');
    if (!img || !node || !node.getBoundingClientRect) return;
    var from = node.getBoundingClientRect();
    if (from.width < 4 || from.height < 4) return;

    var go = function () {
      var to = img.getBoundingClientRect();
      if (to.width < 4) return;
      var k = from.width / to.width;
      var dx = (from.left + from.width / 2) - (to.left + to.width / 2);
      var dy = (from.top + from.height / 2) - (to.top + to.height / 2);
      move(img,
           'translate(' + dx + 'px,' + dy + 'px) scale(' + k + ')',
           'translate(0px,0px) scale(1)',
           300, 'cubic-bezier(.2,.75,.3,1)');
    };
    if (img.complete && img.naturalWidth) go();
    else img.addEventListener('load', go, { once: true });
  }

  function close() {
    if (!box || !box.classList.contains('is-open') || shutting) return;
    shutting = true;
    stopPlay();
    if (document.fullscreenElement) document.exitFullscreen().catch(function () {});

    var img = stage.querySelector('.rlb__slide.is-active img');
    var node = sources[at];
    var back = node && node.getBoundingClientRect ? node.getBoundingClientRect() : null;
    var onPage = back && back.width > 4 &&
                 back.bottom > 0 && back.top < window.innerHeight;

    box.classList.add('is-closing');
    dark(false, 250);                       // the page comes back out of the dark
    chrome().forEach(function (el) { fade(el, 1, 0, 170); });

    if (img && onPage && scale <= 1.01) {
      var to = img.getBoundingClientRect();
      var k = back.width / to.width;
      var dx = (back.left + back.width / 2) - (to.left + to.width / 2);
      var dy = (back.top + back.height / 2) - (to.top + to.height / 2);
      move(img,
           getComputedStyle(img).transform,
           'translate(' + dx + 'px,' + dy + 'px) scale(' + k + ')',
           280, 'cubic-bezier(.35,.06,.3,1)');
      after(290);
    } else {
      if (img) fade(img, 1, 0, 200);
      after(210);
    }
  }

  /* Tearing down belongs to the viewer that asked for it. If the shopper taps
     another photograph while this one is still flying back, the old teardown
     must not pull the new viewer down with it. */
  function after(ms) {
    var mine = session;
    setTimeout(function () { if (mine === session) finish(); }, ms);
  }

  function finish() {
    box.style.removeProperty('transform');
    box.style.removeProperty('width');
    box.style.removeProperty('height');
    box.getAnimations && box.getAnimations().forEach(function (a) { a.cancel(); });
    box.style.removeProperty('background-color');
    chrome().forEach(function (el) {
      el.getAnimations && el.getAnimations().forEach(function (a) { a.cancel(); });
      el.style.removeProperty('opacity');
    });
    box.classList.remove('is-open', 'is-closing', 'is-zoomed', 'is-dragging');
    document.documentElement.classList.remove('rlb-lock');
    stage.innerHTML = '';
    thumbsEl.innerHTML = '';
    scale = 1; panX = panY = 0;
    shutting = false;
    if (opener && opener.focus) opener.focus({ preventScroll: true });
    opener = null;
  }

  /* ---------- the controls ---------- */
  function wire() {
    box.querySelector('[data-rlb-close]').addEventListener('click', close);
    /* The stage covers the whole screen, so the dimmed page around the
       photograph is really the stage. Clicking there closes as well, but only
       when the photograph itself was not the thing clicked, and never while a
       zoomed photograph is being dragged about. */
    stage.addEventListener('click', function (e) {
      if (e.target.tagName === 'IMG') return;
      if (scale > 1.01) return;
      close();
    });
    prevBtn.addEventListener('click', function () { stopPlay(); step(-1); });
    nextBtn.addEventListener('click', function () { stopPlay(); step(1); });
    zoomBtn.addEventListener('click', function () {
      setZoom(scale > 1.01 ? 1 : ZOOM, stage.clientWidth / 2, stage.clientHeight / 2);
    });
    playBtn.addEventListener('click', function () { playing() ? stopPlay() : startPlay(); });
    box.querySelector('[data-rlb-full]').addEventListener('click', function () {
      if (document.fullscreenElement) document.exitFullscreen().catch(function () {});
      else if (box.requestFullscreen) box.requestFullscreen().catch(function () {});
    });
    box.querySelector('[data-rlb-thumbs-toggle]').addEventListener('click', function () {
      box.classList.toggle('is-thumbs-off');
    });

    document.addEventListener('keydown', function (e) {
      if (!box.classList.contains('is-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowRight') { stopPlay(); step(1); }
      else if (e.key === 'ArrowLeft') { stopPlay(); step(-1); }
    });

    /* Touch and mouse: a swipe steps, a drag moves a zoomed photograph, a
       pinch zooms, and a double tap zooms in and out again.

       These listen on the whole viewer rather than on the photograph alone,
       because a thumb swiping in from the edge of the screen usually starts on
       top of a round arrow, and an arrow that swallowed the swipe would leave
       the shopper stuck. The toolbar and the thumbnail strip are left out: they
       are scrolled and tapped in their own right. */
    var pts = new Map(), startX = 0, startY = 0, basePanX = 0, basePanY = 0;
    var pinchFrom = 0, baseScale = 1, moved = 0, lastTap = 0, onArrow = false;

    var offLimits = function (t) {
      return !!(t.closest && t.closest('.rlb__bar, .rlb__thumbs'));
    };

    var dist = function () {
      var a = [...pts.values()];
      return Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y);
    };
    var mid = function () {
      var a = [...pts.values()];
      return { x: (a[0].x + a[1].x) / 2, y: (a[0].y + a[1].y) / 2 };
    };

    box.addEventListener('pointerdown', function (e) {
      if (offLimits(e.target)) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pts.size === 1) {
        onArrow = !!(e.target.closest && e.target.closest('.rlb__nav'));
        startX = e.clientX; startY = e.clientY;
        basePanX = panX; basePanY = panY; moved = 0;
      } else if (pts.size === 2) {
        pinchFrom = dist(); baseScale = scale;
      }
      box.classList.add('is-dragging');
    });

    box.addEventListener('pointermove', function (e) {
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pts.size === 2 && pinchFrom) {
        var m = mid();
        setZoom(baseScale * (dist() / pinchFrom), m.x, m.y);
        return;
      }
      var dx = e.clientX - startX, dy = e.clientY - startY;
      moved = Math.max(moved, Math.hypot(dx, dy));
      if (scale > 1.01) {
        panX = basePanX + dx;
        panY = basePanY + dy;
        clampPan();
        paint();
      }
    });

    /* A swipe that started on an arrow already moved the photograph; the click
       the browser sends afterwards would move it a second time. */
    var swallowNextClick = function () {
      var once = function (ev) {
        ev.stopPropagation();
        ev.preventDefault();
        done();
      };
      var done = function () {
        clearTimeout(t);
        box.removeEventListener('click', once, true);
      };
      // If the browser sends no click at all, the guard must not lie in wait
      // for a real one later on.
      var t = setTimeout(done, 400);
      box.addEventListener('click', once, true);
    };

    var release = function (e) {
      if (!pts.has(e.pointerId)) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      pts.delete(e.pointerId);
      if (pts.size === 0) box.classList.remove('is-dragging');
      if (pts.size >= 1) { pinchFrom = 0; return; }

      if (scale <= 1.01) {
        if (Math.abs(dx) > SWIPE_MIN && Math.abs(dx) > Math.abs(dy)) {
          stopPlay();
          step(dx < 0 ? 1 : -1);
          if (onArrow) swallowNextClick();
          return;
        }
        if (moved < TAP_SLOP && !onArrow) {
          var now = Date.now();
          if (now - lastTap < 300) {
            lastTap = 0;
            setZoom(ZOOM, e.clientX, e.clientY);
          } else {
            lastTap = now;
          }
        }
      } else if (moved < TAP_SLOP) {
        var t = Date.now();
        if (t - lastTap < 300) { lastTap = 0; setZoom(1); }
        else lastTap = t;
      }
    };
    box.addEventListener('pointerup', release);
    box.addEventListener('pointercancel', function (e) {
      pts.delete(e.pointerId);
      if (!pts.size) box.classList.remove('is-dragging');
    });

    window.addEventListener('resize', function () {
      if (box.classList.contains('is-open')) { anchor(); clampPan(); paint(); }
    }, { passive: true });
  }

  /* ---------- the gallery this viewer belongs to ---------- */
  function shotsIn(gallery) {
    return [...gallery.querySelectorAll('[data-zoom-full]')].map(function (m) {
      var im = m.querySelector('img');
      return {
        full: m.getAttribute('data-zoom-full'),
        thumb: m.getAttribute('data-zoom-thumb'),
        alt: m.getAttribute('data-alt') || '',
        // what the page is already showing: it is in the cache, so the
        // opening zoom can start on the very next frame
        preview: im ? (im.currentSrc || im.src) : '',
        node: im || m
      };
    });
  }

  function attach(gallery) {
    if (gallery.dataset.rlbReady === '1') return;
    gallery.dataset.rlbReady = '1';

    // The phone gallery is a swipe carousel, so a drag must not count as a tap.
    var downX = 0, downY = 0;
    gallery.addEventListener('pointerdown', function (e) {
      downX = e.clientX; downY = e.clientY;
    });
    gallery.addEventListener('click', function (e) {
      var media = e.target.closest('[data-zoom-full]');
      if (!media || !gallery.contains(media)) return;
      if (Math.hypot(e.clientX - downX, e.clientY - downY) > TAP_SLOP) return;
      e.preventDefault();
      var list = shotsIn(gallery);
      var index = [...gallery.querySelectorAll('[data-zoom-full]')].indexOf(media);
      open(list, Math.max(0, index), media);
    });
    gallery.querySelectorAll('[data-zoom-full]').forEach(function (m) {
      m.style.cursor = 'zoom-in';
    });
  }

  function scan(root) {
    (root || document).querySelectorAll('[data-rpd-gallery]').forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });

  window.refLightbox = { open: open, close: close };
}());
